'use client';

import React from 'react';
import { Badge } from '../common/Badge';
import * as SubframeUtils from '../utils';

interface AgentFlowStep {
  title: string;
  description: string;
  requirements?: string[];
}

interface AgentSummaryCardProps {
  title: string;
  specialty?: string;
  description: string;
  successRate: string | number;
  avgTime: string;
  icon: React.ReactNode;
  flowSteps?: AgentFlowStep[];
  className?: string;
}

export function AgentSummaryCard({
  title,
  specialty,
  description,
  successRate,
  avgTime,
  icon,
  flowSteps = [],
  className,
}: AgentSummaryCardProps) {
  const formattedSuccessRate =
    typeof successRate === 'number'
      ? `${Math.round(successRate <= 1 ? successRate * 100 : successRate)}%`
      : successRate;

  return (
    <div
      className={SubframeUtils.twClassNames(
        'desktop-lg:flex hidden h-full flex-col overflow-hidden rounded-[16px] border border-solid border-neutral-200 bg-white shadow-sm',
        className,
      )}
    >
      {/* Header */}
      <div className="flex flex-col gap-4 border-b border-solid border-neutral-100 bg-neutral-50 p-6">
        <div className="flex items-start gap-3">
          <div className="text-brand-600 flex h-10 w-10 flex-none items-center justify-center rounded-[12px] border border-solid border-neutral-100 bg-white shadow-sm">
            {icon}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <h3 className="text-body-bold font-body-bold text-default-font">{title}</h3>
            {specialty && (
              <span className="text-caption font-caption text-subtext-color">{specialty}</span>
            )}
          </div>
        </div>

        <p className="text-caption text-subtext-color leading-relaxed">{description}</p>

        <div className="flex gap-6">
          <div className="flex flex-col gap-0.5">
            <span className="text-subtext-color text-[10px] font-medium tracking-wider uppercase">
              Taxa de Sucesso
            </span>
            <span className="text-body-bold font-body-bold text-success-700">
              {formattedSuccessRate}
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-subtext-color text-[10px] font-medium tracking-wider uppercase">
              Tempo Médio
            </span>
            <span className="text-body-bold font-body-bold text-default-font">{avgTime}</span>
          </div>
        </div>
      </div>

      {/* Body com Roteiro */}
      {flowSteps.length > 0 && (
        <div className="flex flex-1 flex-col gap-4 overflow-auto p-6">
          <div className="flex flex-col gap-1">
            <h4 className="text-default-font text-[10px] font-bold tracking-wider uppercase">
              Roteiro de Execução
            </h4>
            <p className="text-caption text-subtext-color">Etapas automatizadas pelo agente.</p>
          </div>

          <div className="flex flex-col gap-0">
            {flowSteps.map((step, index) => {
              const isLast = index === flowSteps.length - 1;
              return (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-neutral-100 text-[11px] font-bold text-neutral-600">
                      {index + 1}
                    </div>
                    {!isLast && <div className="my-1 w-px grow bg-neutral-100" />}
                  </div>
                  <div
                    className={SubframeUtils.twClassNames('flex flex-col gap-1 pb-6', {
                      'pb-3': isLast,
                    })}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="text-caption-bold font-caption-bold text-default-font">
                        {step.title}
                      </span>
                      <span className="text-caption text-subtext-color leading-relaxed">
                        {step.description}
                      </span>
                    </div>
                    {step.requirements && step.requirements.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {step.requirements.map((req, ridx) => (
                          <Badge
                            key={ridx}
                            variant="neutral"
                            className="border-neutral-200 bg-neutral-50 px-1.5 py-0 text-[10px]"
                          >
                            {req}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
